import { Group, NumberInput, Radio, Stack } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useEffect, useState } from "react";
import { tomorrow } from "shared/utils/date";
import { maybe } from "shared/utils/maybe";
import { DurationNode } from "src/gql/graphql";
import { DurationInfoInput } from "src/gql/zod";
import { z } from "zod";

type DurationEditorProps = {
    value?: DurationNode;
    minDate?: Date;
    onChange: (v: DurationInfoInput) => void;
    error?: string;
};

export const DurationEditor = ({ value, minDate, onChange, error }: DurationEditorProps) => {
    const initialMode = value?.span ? "span" : "absolute";
    const initialExpireAt = value?.absolute ? new Date(Number(value.absolute)) : tomorrow();
    const initialExpireIn = value?.span ?? 1440; // 24 hours

    const [mode, setMode] = useState<"absolute" | "span">(initialMode);
    const [expireAt, setExpireAt] = useState<Date>(initialExpireAt);
    const [expireIn, setExpireIn] = useState<number>(initialExpireIn);

    useEffect(() => {
        if (mode == "absolute") onChange({ span: null, absolute: expireAt.getTime().toString() });
        if (mode == "span") onChange({ absolute: null, span: expireIn });
    }, [mode, expireAt, expireIn]);

    useEffect(() => {
        if (mode === "span") setExpireAt(tomorrow());
        else setExpireIn(1440);
    }, [mode]);

    return (
        <Stack gap="sm">
            <Group>
                <Radio checked={mode === "absolute"} onChange={() => setMode("absolute")} label="At Specific Time" />
                <DateTimePicker
                    valueFormat="MMM DD YYYY hh:mm A"
                    value={expireAt}
                    onChange={(value) => value && setExpireAt(z.coerce.date().parse(value))}
                    minDate={minDate}
                    disabled={mode !== "absolute"}
                    error={error}
                />
            </Group>
            <Group>
                <Radio checked={mode === "span"} onChange={() => setMode("span")} label="After" />
                <NumberInput
                    value={expireIn / 60}
                    onChange={(val) => {
                        setExpireIn(
                            maybe(parseFloat(val.toString()))
                                .if((it) => !isNaN(it))
                                ?.take((it) => it * 60) ?? initialExpireIn,
                        );
                    }}
                    decimalScale={1}
                    min={0.5}
                    step={0.5}
                    disabled={mode !== "span"}
                    suffix={expireIn > 60 ? " Hours" : " Hour"}
                    aria-label="Duration in hours"
                />
            </Group>
        </Stack>
    );
};
